// Forced TURN (FORCE_TURN_ALL, dedicated edition, 2026-10-24): every session relays through the platform's TURN
// servers. The platform says so twice, and the player honours both:
//
//   * the ticket's config carries forceTurn=true (platform-api) -> App.js sets Flags.ForceTURN, as it always did for a
//     project with Force TURN on;
//   * signalling's config message carries peerConnectionOptions.iceTransportPolicy 'relay'. Epic's lib copies those
//     options into its RTCPeerConnection, so the browser already gathers relay candidates only. This module ALSO turns
//     Flags.ForceTURN on when that happens, so the lib's own forced-TURN rules apply whatever the ticket said (an older
//     platform-api, a cached config): it refuses to start without a TURN server, and drops any non-relay remote
//     candidate. Without the policy in the config nothing changes (SaaS).
//
// And signalling's "no relay to give you" — close 4010 "Relay unavailable", sent only with FORCE_TURN_ALL on — is a
// terminal close: the lib's own reconnect is switched off for it (a reconnect would be refused the same way), and the
// viewer gets a plain sentence instead of a bare code.
//
// CommonJS and dependency-free so plain node can test it (test/relay-policy.js), like ./admission.js.

const RELAY_UNAVAILABLE_CODE = 4010;
const RELAY_UNAVAILABLE_REASON = 'Relay unavailable';

function relayRequired(peerConnectionOptions) {
  return Boolean(peerConnectionOptions) && peerConnectionOptions.iceTransportPolicy === 'relay';
}

function isRelayUnavailable(code) {
  return Number(code) === RELAY_UNAVAILABLE_CODE;
}

function relayUnavailableMessage() {
  return 'The secure relay for this stream is unavailable right now. Please try again in a few minutes.';
}

// `controller`: the lib's WebRtcPlayerController (pixelStreaming._webRtcController); `config`: the lib Config;
// `forceTurnFlag`: Flags.ForceTURN. Returns false (and changes nothing) when the controller is not the expected shape.
function installRelayPolicy(controller, config, forceTurnFlag) {
  if (!controller || typeof controller.handleOnConfigMessage !== 'function' || !config || typeof config.setFlagEnabled !== 'function') {
    return false;
  }
  if (controller.__spxRelayPolicyInstalled) return true;
  controller.__spxRelayPolicyInstalled = true;

  const originalConfig = controller.handleOnConfigMessage;
  controller.handleOnConfigMessage = function handleOnConfigMessageWithRelayPolicy(message, ...rest) {
    if (message && relayRequired(message.peerConnectionOptions)) {
      try {
        if (!config.isFlagEnabled || !config.isFlagEnabled(forceTurnFlag)) config.setFlagEnabled(forceTurnFlag, true);
      } catch (e) { /* the lib's options still carry the policy */ }
    }
    return originalConfig.call(controller, message, ...rest);
  };

  const transport = controller.transport || (controller.protocol && controller.protocol.transport);
  if (transport && typeof transport.handleOnClose === 'function' && !transport.__spxRelayCloseInstalled) {
    transport.__spxRelayCloseInstalled = true;
    const originalClose = transport.handleOnClose;
    transport.handleOnClose = function handleOnCloseWithRelayPolicy(event, ...more) {
      if (event && isRelayUnavailable(event.code)) {
        controller.enableAutoReconnect = false;
        controller.forceReconnect = false;
      }
      return originalClose.call(transport, event, ...more);
    };
  }
  return true;
}

module.exports = {
  RELAY_UNAVAILABLE_CODE, RELAY_UNAVAILABLE_REASON, relayRequired, isRelayUnavailable, relayUnavailableMessage, installRelayPolicy,
};
